// React imports
import { useState, useEffect, useMemo } from 'react';

// MUI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';

// Generic imports
import validator from 'email-validator';
import axios from 'axios';
import Editor from 'react-simple-wysiwyg';
import _ from 'lodash';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import email from '../utils/email';
import ZPopper from './ZPopper.js';

function SendEmailUsingExternalServiceDialog(props) {
    const { org, accesstoken, emailLoginInfo, onEmailRefreshConnection, onEmailLogout, 
        toRecipients, ccRecipients, initialBody, initialSubject, onClose, buttons, buttonsJustify,
        editorFooter, footer, receipientEditable, onReceipientChange, subjectEditable = false,
        accounts, selectedAccountId, noBorderBottom = false, previewMode = false,
     } = props;

    // Shared field-label + horizontal-padding styling for a clean, professional compose layout.
    const labelSx = { width: '64px', minWidth: '64px', flexShrink: 0, fontSize: '13px', fontWeight: 500, color: '#64748b' };
    const ROW_PX = '24px';
    const isConnected = emailLoginInfo?.status === 'connected';

    const [ body, setBody ] = useState(initialBody);
    const [ subject, setSubject ] = useState(initialSubject);
    const [ stateToRecipients, setStateToRecipients ] = useState(toRecipients);
    const [ stateCcRecipients, setStateCcRecipients ] = useState(ccRecipients);
    const [ thinking, setThinking ] = useState(false);
    const [ emailError, setEmailError ] = useState(false);
    const [ ccEmailError, setCcEmailError ] = useState(false);

    const selectedAccount = selectedAccountId ? accounts.find(a => a.id === selectedAccountId) : null;

    useEffect(() => {
        setStateToRecipients(toRecipients || []);
        setStateCcRecipients(ccRecipients || []);
    }, [JSON.stringify(toRecipients), JSON.stringify(ccRecipients)]);

    const allRecipients = useMemo(() => {
        return _.uniqBy([
            ...(selectedAccount ? (selectedAccount.contacts || []).map(c => ({ address: c.email, name: `${c.firstName ? `${c.firstName} ` : ''}${c.lastName ? `${c.lastName}` : ''}${c.firstName || c.lastName ? ' at ' : 'Contact at '}${selectedAccount.name ? `${selectedAccount.name}` : ''}` })) : []),
            ...(accounts.map(a => (a.contacts || []).map(c => ({ address: c.email, name: `${c.firstName ? `${c.firstName} ` : ''}${c.lastName ? `${c.lastName}` : ''}${c.firstName || c.lastName ? ' at ' : 'Contact at '}${a.name ? `${a.name}` : ''}` })))).flat(),
        ], a => a.address);
    }, [accounts, selectedAccountId]);

    useEffect(() => {
        if (_.isEmpty(stateToRecipients)) {
            return;
        }

        if (_.some(stateToRecipients, r => !validator.validate(r.address))) {
            setEmailError(true);
            return;
        }

        if (_.some(stateCcRecipients, r => !validator.validate(r.address))) {
            setCcEmailError(true);
            return;
        }

        setEmailError(false);
        setCcEmailError(false);

        if (onReceipientChange) {
            (async () => {
                setThinking(true);
                const newBody = await onReceipientChange({ newToRecipients: stateToRecipients, newCcRecipients: stateCcRecipients, currentBody: body });
                setBody(newBody);
                setThinking(false);
            })();
        }
    }, [JSON.stringify(stateToRecipients), JSON.stringify(stateCcRecipients)]);

    async function _setStateToRecipients(newToRecipients) {
        setStateToRecipients(newToRecipients);
    }

    async function _setStateCcRecipients(newCcRecipients) {
        setStateCcRecipients(newCcRecipients);
    }

    const onBodyChange = (event) => {
        setBody(event.target.value);
    };

    const onSubjectChange = (event) => {
        setSubject(event.target.value);
    };

    if (!previewMode && !emailLoginInfo) return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
        </Box>
    );

    if (!previewMode && emailLoginInfo.status !== 'connected') return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
            <Typography variant='h6' sx={{ mb: 2 }}>Connect Your Email Account</Typography>
            <email.LoginButton
                org={org}
                accesstoken={accesstoken}
                onDone={onEmailRefreshConnection}
            />
        </Box>
    )

    // This editor is a plain contentEditable, so a pasted screenshot lands as a base64
    // data: URI - which Gmail and Outlook both strip, leaving a gap where the image was.
    // Signatures hit this first and solved it by re-hosting; do the same here so an image
    // pasted into an RFQ or a quote email actually reaches the recipient.
    //
    // Markup wins over the picture, the same rule the quote builder uses: Excel puts a
    // rendering of the selection on the clipboard beside the real HTML, and taking the
    // image would turn a pasted table into a screenshot of one.
    async function onPasteImage(event) {
        const clipboard = event.clipboardData;
        const html = (clipboard?.getData('text/html') || '').trim();
        const text = (clipboard?.getData('text/plain') || '').trim();
        if (html || text) return;

        const files = [...(clipboard?.files || [])].filter(f => f.type.startsWith('image/'));
        if (files.length === 0) return;
        event.preventDefault();

        for (const file of files) {
            const dataUrl = await new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(file);
            });
            if (!dataUrl) continue;

            // Outlook sizes a remote image from its attributes and mishandles one carrying
            // neither, so measure before inserting. Capped to a usual mail content width.
            const size = await new Promise(resolve => {
                const probe = new window.Image();
                probe.onload = () => {
                    const w = probe.naturalWidth || 0, h = probe.naturalHeight || 0;
                    if (!w || !h) return resolve(null);
                    const scale = Math.min(1, 600 / w);
                    resolve({ width: Math.round(w * scale), height: Math.round(h * scale) });
                };
                probe.onerror = () => resolve(null);
                probe.src = dataUrl;
            });

            let src = dataUrl;
            try {
                const { data } = await axios.post(
                    `/api/users/signature-image?accesstoken=${accesstoken}&organizationId=${org.id}`,
                    { image: dataUrl, contentType: file.type, prefix: 'email-images' }
                );
                if (data?.url) src = data.url;
            } catch (e) {
                // Keep the inline copy rather than losing the paste; it may still render for
                // some recipients, and the alternative is nothing at all.
                console.error('[send email] Image upload failed, pasting inline:', e);
            }

            const dimensions = size ? ` width="${size.width}" height="${size.height}"` : '';
            document.execCommand('insertHTML', false, `<img src="${src}"${dimensions} style="max-width:100%;height:auto;" />`);
        }
    }

    return (
        <Box sx={{ 
            maxWidth: '75vw', 
            minWidth: '75vw', 
            display: 'flex', 
            flexDirection: 'column', 
            pt: 2, 
            pb: 2, 
            position: 'relative',
            '& .MuiChip-label': {
                fontSize: '11px',
                fontFamily: 'Poppins, sans-serif',
            },
            '& .MuiAutocomplete-option': {
                fontSize: '11px',
                fontFamily: 'Poppins, sans-serif',
            },
            // Email body editor: readable (not oversized) font and a clear frame.
            '& .rsw-editor': {
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                margin: '0px 24px',
            },
            '& .rsw-ce': {
                fontSize: '13px',
                fontFamily: 'Poppins, sans-serif',
            },
            '& .rsw-toolbar': {
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
            },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, padding: `0px ${ROW_PX}` }}>
                <Box sx={labelSx}>From</Box>
                <Box sx={{ mr: 2, fontSize: '13px' }}>
                    {isConnected
                        ? <>{emailLoginInfo.displayName} &lt;{emailLoginInfo.email}&gt;</>
                        : <i style={{ color: '#94a3b8' }}>Preview mode — connect a mailbox to send</i>}
                </Box>
                {isConnected && (
                    <Button onClick={onEmailLogout} sx={{ minWidth: '0px', padding: '0px', textTransform: 'none', textDecoration: 'underline', fontSize: '13px' }}>Logout</Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {onClose && (
                    <Button onClick={onClose} color='error' sx={{ minWidth: '0px', padding: '0px' }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </Button>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, padding: `0px ${ROW_PX}`, alignItems: 'flex-start' }}>
                <Box sx={{ ...labelSx, marginTop: '12px' }}>To</Box>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, marginTop: '6px' }}>
                    <Autocomplete
                        fullWidth
                        options={allRecipients.filter(r => !([...(stateToRecipients || []), ...(stateCcRecipients || [])].map(a => a.address).includes(r.address)))}
                        filterOptions={(options, state) => {
                            if (!state.inputValue && selectedAccount) {
                                return options.filter(o => o.name.endsWith(' at ' + selectedAccount?.name));
                            }
                            return options
                                .filter((o) =>
                                    o.name?.toLowerCase().includes(state.inputValue.toLowerCase()) || o.address?.toLowerCase().includes(state.inputValue.toLowerCase())
                                )
                                .slice(0, 100)
                        }
                        }
                        multiple={receipientEditable === 'multiple'}
                        freeSolo
                        value={receipientEditable === 'single' ? stateToRecipients[0] : stateToRecipients || []}
                        onChange={(___, value) => {
                            if (receipientEditable === 'single') {
                                _setStateToRecipients([value]);
                            } else {
                                _setStateToRecipients(value.map(v => _.isString(v) ? { address: _.trim(v) } : v))
                            }
                        }}
                        onBlur={(a) => {
                            const v = _.trim(a.target.value);
                            if (!v) return null;
                            if (receipientEditable === 'single') {
                                _setStateToRecipients([v]);
                            } else {
                                _setStateToRecipients(s => v ? [...s, { address: v }] : [])
                            }
                        }}
                        clearOnBlur
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.name ? `${option.name} <${option.address}>` : option.address;
                        }}
                        renderInput={(params) => <TextField {...params} />}
                        groupBy={(option) => {
                            if (selectedAccount && option.name.endsWith(' at ' + selectedAccount?.name)) {
                                return 'Opportunity Account Contacts';
                            } else if (selectedAccount) {
                                return 'Other Accounts Contacts';
                            } else {
                                return null;
                            }
                        }}
                        slots={{
                            popper: ZPopper
                        }}
                        PopperComponent={ZPopper}
                        slotProps={{
                            paper: {
                                sx: {
                                    '& .MuiAutocomplete-option': {
                                        fontSize: '11px',
                                        fontFamily: 'Poppins, sans-serif',
                                    },
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
            {emailError && (
                <Alert severity='error' sx={{ mx: '24px' }}>Please enter a valid email address</Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, padding: `0px ${ROW_PX}`, alignItems: 'flex-start' }}>
                <Box sx={{ ...labelSx, marginTop: '12px' }}>Cc</Box>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, marginTop: '6px' }}>
                    <Autocomplete
                        fullWidth
                        options={allRecipients.filter(r => !([...(stateToRecipients || []), ...(stateCcRecipients || [])].map(a => a.address).includes(r.address)))}
                        filterOptions={(options, state) => {
                            if (!state.inputValue && selectedAccount) {
                                return options.filter(o => o.name.endsWith(' at ' + selectedAccount?.name));
                            }
                            return options
                                .filter((o) =>
                                    o.name?.toLowerCase().includes(state.inputValue.toLowerCase()) || o.address?.toLowerCase().includes(state.inputValue.toLowerCase())
                                )
                                .slice(0, 100)
                        }
                        }
                        multiple
                        freeSolo
                        value={stateCcRecipients || []}
                        onChange={(___, value) => {
                            _setStateCcRecipients(value.map(v => _.isString(v) ? { address: _.trim(v) } : v))
                        }}
                        groupBy={(option) => {
                            if (selectedAccount && option.name.endsWith(' at ' + selectedAccount?.name)) {
                                return 'Opportunity Account Contacts';
                            } else if (selectedAccount) {
                                return 'Other Accounts Contacts';
                            } else {
                                return null;
                            }
                        }}
                        onBlur={(a) => {
                            const v = _.trim(a.target.value);
                            if (!v) return null;
                            if (receipientEditable === 'single') {
                                _setStateCcRecipients([v]);
                            } else {
                                _setStateCcRecipients(s => v ? [...s, { address: v }] : [])
                            }
                        }}
                        clearOnBlur
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.name ? `${option.name} <${option.address}>` : option.address;
                        }}
                        renderInput={(params) => <TextField {...params} />}
                        slots={{
                            popper: ZPopper
                        }}
                        PopperComponent={ZPopper}
                        slotProps={{
                            paper: {
                                sx: {
                                    '& .MuiAutocomplete-option': {
                                        fontSize: '11px',
                                        fontFamily: 'Poppins, sans-serif',
                                    },
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
            {ccEmailError && (
                <Alert severity='error' sx={{ mx: '24px' }}>Please enter a valid email address</Alert>
            )}
            {!subjectEditable && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1.5, padding: `0px ${ROW_PX}`, alignItems: 'center', fontSize: '13px' }}>
                    <Box sx={labelSx}>Subject</Box>
                    <Box>{subject || <i>No subject</i>}</Box>
                </Box>
            )}
            {subjectEditable && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, padding: `0px ${ROW_PX}`, alignItems: 'flex-start' }}>
                    <Box sx={{ ...labelSx, marginTop: '12px' }}>Subject</Box>
                    <TextField value={subject} onChange={onSubjectChange} fullWidth size='small' sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }} />
                </Box>
            )}
            <Box sx={{ borderTop: '1px solid #e2e8f0', mx: ROW_PX, mb: 1.5 }} />
            <Box onPaste={onPasteImage}>
                <Editor
                    containerProps={noBorderBottom ? { style: { borderBottom: 'none', borderRadius: '4px 4px 0 0'} } : {}}
                    value={body}
                    onChange={onBodyChange}
                />
            </Box>
            {editorFooter && <Box sx={{ px: ROW_PX, mt: 1.5 }}>{editorFooter}</Box>}
            {previewMode && (
                <Alert severity='info' sx={{ mx: '24px', mt: 1 }}>
                    Preview mode (localhost) — sending is disabled. Connect a mailbox on alpha to send for real.
                </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: buttonsJustify ? buttonsJustify : 'flex-end', gap: 1, mt: 2, padding: `0px ${ROW_PX}` }}>
                {buttons.map(b => <Button {...b} disabled={previewMode || b.disabled} onClick={() => {
                    if (_.isEmpty(stateToRecipients) || _.some(stateToRecipients, r => !validator.validate(r.address))) {
                        setEmailError(true);
                        return;
                    }
                    if (_.some(stateCcRecipients, r => !validator.validate(r.address))) {
                        setCcEmailError(true);
                        return;
                    }
                    b.func({ body, subject, toRecipients: stateToRecipients, ccRecipients: stateCcRecipients })
                }} />)}
            </Box>
            {footer}
            {thinking && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)' }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
}

export default SendEmailUsingExternalServiceDialog;